/**
 * SelectWidget component.
 * @module components/manage/Widgets/SelectWidget
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { map } from 'lodash';
import { defineMessages, injectIntl } from 'react-intl';
import {
    getVocabFromHint,
    getVocabFromField,
    getVocabFromItems,
} from '@plone/volto/helpers';
import { getApiURL } from 'design-comuni-plone-theme/components';
import { FormFieldWrapper } from '@plone/volto/components';
import { getVocabulary, getVocabularyTokenTitle } from '@plone/volto/actions';
import { normalizeValue } from '@plone/volto/components/manage/Widgets/SelectUtils';

import {
    customSelectStyles,
    DropdownIndicator,
    ClearIndicator,
    Option,
    selectTheme,
    MenuList,
    MultiValueContainer,
} from '@plone/volto/components/manage/Widgets/SelectStyling';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import config from '@plone/volto/registry';
import warningSVG from '../../theme/images/warning.svg';

const messages = defineMessages({
    default: {
        id: 'Default',
        defaultMessage: 'Default',
    },
    idTitle: {
        id: 'Short Name',
        defaultMessage: 'Short Name',
    },
    idDescription: {
        id: 'Used for programmatic access to the fieldset.',
        defaultMessage: 'Used for programmatic access to the fieldset.',
    },
    title: {
        id: 'Title',
        defaultMessage: 'Title',
    },
    description: {
        id: 'Description',
        defaultMessage: 'Description',
    },
    close: {
        id: 'Close',
        defaultMessage: 'Close',
    },
    choices: {
        id: 'Choices',
        defaultMessage: 'Choices',
    },
    required: {
        id: 'Required',
        defaultMessage: 'Required',
    },
    select: {
        id: 'Select…',
        defaultMessage: 'Select…',
    },
    no_value: {
        id: 'No value',
        defaultMessage: 'No value',
    },
    no_options: {
        id: 'No options',
        defaultMessage: 'No options',
    },
});

const PARENT_ID = "responsabile-parent";
const MIDDLE_ID = "responsabile-middle";
const FIELD_ID = "responsabile-field";

/**
 * SelectWidget component class.
 * @function SelectStrutturaWidget
 * @returns {string} Markup of the component.
 */
class SelectStrutturaWidget extends Component {
    /**
     * Property types.
     * @property {Object} propTypes Property types.
     * @static
     */
    static propTypes = {
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        required: PropTypes.bool,
        error: PropTypes.arrayOf(PropTypes.string),
        getVocabulary: PropTypes.func.isRequired,
        getVocabularyTokenTitle: PropTypes.func.isRequired,
        choices: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        ),
        items: PropTypes.shape({
            vocabulary: PropTypes.object,
        }),
        widgetOptions: PropTypes.shape({
            vocabulary: PropTypes.object,
        }),
        value: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.string,
            PropTypes.bool,
            PropTypes.func,
            PropTypes.array,
        ]),
        onChange: PropTypes.func.isRequired,
        onBlur: PropTypes.func,
        onClick: PropTypes.func,
        onEdit: PropTypes.func,
        onDelete: PropTypes.func,
        wrapped: PropTypes.bool,
        noValueOption: PropTypes.bool,
        customOptionStyling: PropTypes.any,
        isMulti: PropTypes.bool,
        placeholder: PropTypes.string,
    };

    /**
     * Default properties
     * @property {Object} defaultProps Default properties.
     * @static
     */
    static defaultProps = {
        description: null,
        required: false,
        items: {
            vocabulary: null,
        },
        widgetOptions: {
            vocabulary: null,
        },
        error: [],
        choices: [],
        value: null,
        onChange: () => { },
        onBlur: () => { },
        onClick: () => { },
        onEdit: null,
        onDelete: null,
        noValueOption: true,
        customOptionStyling: null,
    };

    /**
    * Component did mount
    * @method componentDidMount
    * @returns {undefined}
    */
    componentDidMount() {
        if (
            (!this.props.choices || this.props.choices?.length === 0) &&
            this.props.vocabBaseUrl
        ) {
            let vocab = this.props.vocabBaseUrl.replace(
                /plone\.app\.vocabularies\.Catalog$/,
                "persone.strutture"
            );
            this.props.getVocabulary({
                vocabNameOrURL: vocab,
                size: -1,
                subrequest: this.props.lang,
            });
        }
        const url = this.extractPathFromUrl(this.props.value?.['@id']);
        if (url) {
            this.getStruttura(url, this.props.formData.id, this.props.formData);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.vocabBaseUrl !== prevProps.vocabBaseUrl &&
            (!this.props.choices || this.props.choices?.length === 0)
        ) {
            let vocab = this.props.vocabBaseUrl.replace(
                /plone\.app\.vocabularies\.Catalog$/,
                "persone.strutture"
            );
            this.props.getVocabulary({
                vocabNameOrURL: vocab,
                size: -1,
                subrequest: this.props.lang,
            });
        }
        const url = this.extractPathFromUrl(this.props.value?.['@id']);
        const prevUrl = this.extractPathFromUrl(prevProps.value?.['@id']);
        if (url && url !== prevUrl) {
            this.getStruttura(url, this.props.formData.id, this.props.formData);
        }
    }

    async getStruttura(url, edit_id, form_data, uid = false) {
        try {
            if (!url) {
                console.warn("URL della struttura non definito: " + url);
                return null;
            }
            let responseData;
            if (uid) {
                const response = await fetch(
                    `${config.settings.publicURL}/++api++/resolveuid/${url}`,
                    {
                        method: 'GET',
                        redirect: 'follow',
                    },
                );
                if (!response.ok) {
                    throw new Error('Errore nel recupero della struttura');
                }
                let strutturaUrl = response.url;
                let strutturaApiUrl = getApiURL(strutturaUrl);
                const response2 = await fetch(strutturaApiUrl, {
                    method: 'GET',
                });

                if (!response2.ok) {
                    throw new Error('Errore nel recupero della struttura');
                }

                responseData = await response2.json();
            } else {
                const response = await fetch(
                    `${config.settings.publicURL}/++api++${url}`,
                    {
                        method: 'GET'
                    }
                );

                if (!response.ok) {
                    throw new Error('Errore nel recupero della struttura');
                }
                responseData = await response.json();
            }
            const responsabileParent = document.getElementById(PARENT_ID);
            if (!responsabileParent) {
                return null;
            }
            if (responseData.responsabile) {
                // SE LA STRUTTURA HA IL RESPONSABILE
                if (form_data.is_responsabile == true) {
                    // SE LA CHECKBOX RESPONSABILE È SELEZIONATA
                    responsabileParent.className = 'strutturaAfferenza-responsabile m-2 p-2 active';
                    responsabileParent.innerHTML = `
                            <img src="${warningSVG}" alt="Warning Icon" />
                            <div id="${MIDDLE_ID}">
                            Si sta dichiarando di essere il Responsabile della struttura. <br/>
                            Attualmente, nel sistema, <a id="${FIELD_ID}" target="_blank" href="/rubrica/${responseData.responsabile.id}">${responseData.responsabile.title}</a> risulta responsabile della struttura selezionata.
                            </div>
                        `;
                } else {
                    // SE LA CHECKBOX RESPONSABILE NON È SELEZIONATA
                    responsabileParent.className = 'strutturaAfferenza-responsabile m-2 p-2';
                    responsabileParent.innerHTML = `
                            <div id="${MIDDLE_ID}">
                            Responsabile della struttura selezionata: <a id="${FIELD_ID}" target="_blank" href="/rubrica/${responseData.responsabile.id}">${responseData.responsabile.title}</a>
                            </div>
                        `;
                }
                if (edit_id) {
                    if (responseData.responsabile.id === edit_id) {
                        // SE IL RESPONSABILE SEI TU
                        responsabileParent.className = 'strutturaAfferenza-responsabile m-2 p-2 already';
                        responsabileParent.innerHTML = `
                                <div id="${MIDDLE_ID}">
                                Sei già il responsabile della struttura selezionata! <a id="${FIELD_ID}" target="_blank" href="/rubrica/${responseData.responsabile.id}">${responseData.responsabile.title}</a>
                                </div>
                            `;
                    }
                }
            } else {
                // SE LA STRUTTURA NON HA IL RESPONSABILE
                if (form_data.is_responsabile == true) {
                    // SE LA CHECKBOX RESPONSABILE È SELEZIONATA
                    responsabileParent.className = 'strutturaAfferenza-responsabile m-2 p-2 active';
                    responsabileParent.innerHTML = `
                            <img src="${warningSVG}" alt="Warning Icon" />
                            <div id="${MIDDLE_ID}">
                            Si sta dichiarando di essere il Responsabile della struttura. <br/>
                            Attualmente, nel sistema, <a id="${FIELD_ID}">Nessuno</a> risulta responsabile della struttura selezionata.
                            </div>
                        `;
                } else {
                    // SE LA CHECKBOX RESPONSABILE NON È SELEZIONATA
                    responsabileParent.className = 'strutturaAfferenza-responsabile m-2 p-2';
                    responsabileParent.innerHTML = `
                            <div id="${MIDDLE_ID}">
                            Responsabile della struttura selezionata: <a id="${FIELD_ID}">Nessuno</a>
                            </div>
                        `;
                }
            }

        } catch (error) {
            console.error('Errore nel recupero della struttura:', error);
        }
    }

    extractPathFromUrl(url) {
        if (!url) {
            return null;
        }
        const index = url.indexOf('/strutture');
        if (index !== -1) {
            return url.slice(index);
        }
        return null;
    }

    extractPathFromLabel(url) {
        if (!url) {
            return null;
        }
        const pattern = /\(.*?\/strutture\/.*?\)/;
        const match = url.match(pattern);
        if (match) {
            const path = match[0];
            const start = path.indexOf('/strutture/');
            return path.substring(start, path.length - 1);
        }
        return null;
    }

    extractPathFromPath(url) {
        if (!url) {
            return null;
        }
        const pattern = /^\/[^/]+\//;
        const match = url.match(pattern);
        if (match) {
            return url.replace(pattern, '/');
        }
        return null;
    }


    /**
     * Render method.
     * @method render
     * @returns {string} Markup for the component.
     */
    render() {
        const { id, choices, value, intl, onChange } = this.props;
        const normalizedValue = normalizeValue(choices, value, intl);
        // Make sure that both disabled and isDisabled (from the DX layout feat work)
        const disabled = this.props.disabled || this.props.isDisabled;
        const Select = this.props.reactSelect.default;

        let options = this.props.vocabBaseUrl
            ? this.props.choices
            : [
                ...map(choices, (option) => ({
                    value: option[0],
                    label:
                        // Fix "None" on the serializer, to remove when fixed in p.restapi
                        option[1] !== 'None' && option[1] ? option[1] : option[0],
                })),
                // Only set "no-value" option if there's no default in the field
                // TODO: also if this.props.defaultValue?
                ...(this.props.noValueOption &&
                    (this.props.default === undefined || this.props.default === null)
                    ? [
                        {
                            label: this.props.intl.formatMessage(messages.no_value),
                            value: 'no-value',
                        },
                    ]
                    : []),
            ];

        const isMulti = this.props.isMulti
            ? this.props.isMulti
            : id === 'roles' || id === 'groups' || this.props.type === 'array';

        return (
            <div key={"strutturaAfferenza_responsabile"}>
                <div className="strutturaAfferenza-responsabile m-2 p-2" id={PARENT_ID}>
                    <div id={MIDDLE_ID}>
                        Responsabile della struttura selezionata: <a id={FIELD_ID}></a>
                    </div>
                </div>
                <FormFieldWrapper {...this.props}>
                    <Select
                        id={`field-${id}`}
                        key={choices}
                        name={id}
                        menuShouldScrollIntoView={false}
                        isDisabled={disabled}
                        isSearchable={true}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isMulti={isMulti}
                        options={options}
                        styles={customSelectStyles}
                        theme={selectTheme}
                        components={{
                            ...(options?.length > 25 && {
                                MenuList,
                            }),
                            MultiValueContainer,
                            DropdownIndicator,
                            ClearIndicator,
                            Option: this.props.customOptionStyling || Option,
                        }}
                        value={normalizedValue}
                        placeholder={
                            this.props.placeholder ??
                            this.props.intl.formatMessage(messages.select)
                        }
                        onChange={(selectedOption) => {
                            const uid = selectedOption.value;
                            this.getStruttura(uid, this.props.formData.id, this.props.formData, true);
                            if (isMulti) {
                                return onChange(
                                    id,
                                    selectedOption.map((el) => el.value),
                                );
                            }
                            return onChange(
                                id,
                                selectedOption && selectedOption.value !== 'no-value'
                                    ? selectedOption.value
                                    : undefined,
                            );
                        }}
                    />
                </FormFieldWrapper>
            </div>
        );
    }
}

export const SelectStrutturaWidgetComponent = injectIntl(SelectStrutturaWidget);

export default compose(
    injectLazyLibs(['reactSelect']),
    connect(
        (state, props) => {
            let vocabBaseUrl = !props.choices
                ? getVocabFromHint(props) ||
                getVocabFromField(props) ||
                getVocabFromItems(props)
                : '';
            if (vocabBaseUrl?.endsWith("plone.app.vocabularies.Catalog")) {
                vocabBaseUrl = vocabBaseUrl.replace(
                    /plone\.app\.vocabularies\.Catalog$/,
                    "persone.strutture"
                );
            }
            const vocabState =
                state.vocabularies?.[vocabBaseUrl]?.subrequests?.[state.intl.locale];

            // If the schema already has the choices in it, then do not try to get the vocab,
            // even if there is one
            if (props.choices) {
                return {
                    choices: props.choices,
                    lang: state.intl.locale,
                };
            } else if (vocabState) {
                return {
                    vocabBaseUrl,
                    choices: vocabState?.items ?? [],
                    lang: state.intl.locale,
                };
                // There is a moment that vocabState is not there yet, so we need to pass the
                // vocabBaseUrl to the component.
            } else if (vocabBaseUrl) {
                return {
                    vocabBaseUrl,
                    lang: state.intl.locale,
                };
            }
            return { lang: state.intl.locale };
        },
        { getVocabulary, getVocabularyTokenTitle },
    ),
)(SelectStrutturaWidgetComponent);
